import React from 'react';
import { css } from '@emotion/react';
import { Button, Dialog, DialogActions, DialogContent } from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import { color } from '@/styles';

const HintButton = css`
  border-radius: 100px;
  color: ${color.dark_gray};
  background-color: var(--button-bg);
  padding: 8px 16px;
  margin-left: 12px;
  white-space: nowrap;

  &:hover {
    background-color: var(--button-bg);
  }
`;

const HintLabel = css`
  margin-left: 4px;
  font-weight: bold;
  font-size: 13px;
`;

const TopText = css`
  font-size: 12px;
  color: ${color.dark_gray};
`;

const SectionCard = css`
  border: 1px solid ${color.border_gray};
  border-radius: 10px;
  padding: 0 12px;
  margin-top: 12px;
`;

const CardPadding = css`
  padding: 12px;
`;

const SectionTitle = (colorCode?: string) => css`
  font-size: 14px;
  font-weight: bold;
  line-height: 1;
  color: ${colorCode ? colorCode : color.dark_gray};
`;

const SectionText = css`
  font-size: 12px;
  color: ${color.dark_gray};
`;

const SectionRow = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
`;

const SectionRowStacked = css`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
`;

const ImagePlaceholder = css`
  min-width: 220px;
  height: 112px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const ImagePlaceholderBelowText = css`
  width: 100%;
  height: 112px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  overflow: hidden;
`;

const SectionImage = css`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const HintTopRow = css`
  display: flex;
  width: 100%;
  align-items: center;
  gap: 12px;
  padding-right: 12px;
`;

const HintTopText = css`
  flex: 1;
`;

const HintTitle = css`
  margin: 12px 0 8px;
  font-size: 15px;
  font-weight: bold;
  color: ${color.dark_gray};

  span {
    font-size: 20px;
    color: #12aeb0;
  }
`;

const HintList = css`
  margin-top: 8px;
  color: ${color.dark_gray};
`;

const HintListItem = css`
  margin-bottom: 8px;
  font-size: 13px;
`;

const HintItemTitle = css`
  display: inline-block;
  font-weight: bold;
  margin-bottom: 2px;
`;

const HintSubList = css`
  margin: 0;
  padding-left: 1.2em;
`;

const GuideLink = css`
  display: block;
  margin-top: 16px;
  text-align: center;
  font-size: 14px;
  font-weight: bold;
  color: #12aeb0;
  text-decoration: underline;
`;

const CloseButton = css`
  min-width: 104px;
  border-radius: 8px;
  background-color: #e5e5e5;
  color: ${color.dark_gray};

  &:hover {
    background-color: #d9d9d9;
  }
`;

const DialogActionsCenter = css`
  justify-content: center;
  padding-bottom: 16px;
`;

export const DataAnomalyHintButton = React.memo(() => {
  const [open, setOpen] = React.useState(false);

  return (
    <>
      <Button css={HintButton} onClick={() => setOpen(true)}>
        <InfoOutlined sx={{ fontSize: 20, color: '#12AEB0' }} />
        <span css={HintLabel}>数値や通知に違和感がある場合</span>
      </Button>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: {
            width: '680px',
            maxWidth: 'calc(100vw - 32px)',
          },
        }}
      >
        <DialogContent>
          <div css={TopText}>
            DFreeは超音波で膀胱を観察し、膀胱が膨らむ様子を10段階で表示します。下腹部の脂肪や姿勢変化に伴う位置ズレにより膀胱を見失うと、数値が上がらない状態が続くことがあります。
          </div>
          <div css={SectionCard}>
            <div css={SectionRow}>
              <div css={SectionRowStacked}>
                <div css={SectionTitle('#12AEB0')}>理想の状態</div>
                <div css={SectionText}>センサーが膀胱をしっかり捉えている</div>
              </div>
              <div css={ImagePlaceholder}>
                <img css={SectionImage} src='/images/dfreeTop/support_1_01.png' alt='理想の状態' />
              </div>
            </div>
          </div>
          <div css={[SectionCard, CardPadding]}>
            <div css={SectionRowStacked}>
              <div css={SectionTitle()}>測定が難しい状態</div>
              <div css={SectionText}>
                側臥位の姿勢や皮膚のたるみ、下腹部の脂肪の厚みにより、超音波センサーが膀胱に届いていない
              </div>
              <div css={ImagePlaceholderBelowText}>
                <img css={SectionImage} src='/images/dfreeTop/support_2_01.png' alt='測定が難しい状態' />
              </div>
            </div>
          </div>
          <div css={SectionCard}>
            <div css={SectionRow}>
              <div css={SectionRowStacked}>
                <div css={SectionTitle()}>排尿後も尿がたまっている場合</div>
                <div css={SectionText}>膀胱内に尿が残っているため、排尿後も高いセンサーが反応し続けている</div>
              </div>
              <div css={ImagePlaceholder}>
                <img css={SectionImage} src='/images/dfreeTop/support_3_01.png' alt='排尿後も尿がたまっている場合' />
              </div>
            </div>
          </div>
          <div css={HintTitle}>
            解決のための<span>3つのヒント</span>
          </div>
          <div css={HintTopRow}>
            <div css={HintTopText}>
              <div css={HintListItem}>
                <span css={HintItemTitle}>1. 「押圧」で安定させる</span>
                <br />
                装着部がズレないよう腹巻や腹帯、少し小さめのおむつ等でDFreeを上から押さえることをお試しください。エコー検査で器具を肌に押し当てるのと同じイメージで、超音波センサが届きやすくなります。
              </div>
            </div>
            <div css={ImagePlaceholder}>
              <img css={SectionImage} src='/images/dfreeTop/support_4_01.png' alt='解決のためのヒント' />
            </div>
          </div>
          <div css={HintList}>
            <div css={HintListItem}>
              <span css={HintItemTitle}>2. 姿勢を整えてみる</span>
              <br />
              一時的に仰臥位や、浅い側臥位になると、膀胱が捉えやすくなる場合があります。
            </div>
            <div css={HintListItem}>
              <span css={HintItemTitle}>3. 装着位置を微調整する</span>
              <ul css={HintSubList}>
                <li>数値がずっと高いままの場合：DFree の位置を1cm上へ移動させてください。</li>
                <li>数値がなかなか上がらない場合：DFree の位置を1cm下へ移動させてください。</li>
              </ul>
            </div>
          </div>
          <a
            css={GuideLink}
            href='https://dfree.biz/professional/support#basics_05_01'
            target='_blank'
            rel='noreferrer'
          >
            上記以外で不明点がある場合は困った時の対処法からご確認ください
          </a>
        </DialogContent>
        <DialogActions css={DialogActionsCenter}>
          <Button css={CloseButton} variant='contained' onClick={() => setOpen(false)}>
            閉じる
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
});
